import { useEffect } from 'react'

import { useCoAgent } from '@copilotkit/react-core'
import { useAiChat } from '@factorialco/f0-react/dist/experimental'

import { useOneCreateActionState } from 'modules/core/lib/oneCreateActions'

export type CoCreationAgentOptions<T> = {
  name: string
  initialState: T
  initialMessage: string
}

export const useCocreationAgent = <ActionState, T>(options: CoCreationAgentOptions<T>) => {
  const { tmp_setAgent, setInitialMessage, agent, clear, setWelcomeScreenSuggestions } = useAiChat()

  // Set the agent
  useEffect(() => {
    if (agent !== options.name) {
      tmp_setAgent(options.name)
      setInitialMessage(options.initialMessage)
      setWelcomeScreenSuggestions([])
      clear()
    }
  }, [tmp_setAgent])

  // Resets the chat when the component unmounts
  useEffect(() => {
    return () => {
      tmp_setAgent('one-workflow')
      requestAnimationFrame(() => {
        clear()
      })
      setInitialMessage(undefined)
    }
  }, [])

  // We try to recover the state from an inline creation
  const { data } = useOneCreateActionState<ActionState>()

  // WARNING: This `setState` function is not referentially stable, don't pass
  // it to a dependency array
  const { state, setState } = useCoAgent<ActionState>({
    name: options.name,
    initialState: data ?? options.initialState,
  })

  return {
    setState,
    state,
    data,
  }
}
